/**
 * A PLACEHOLDER ASK IS NOT A PRICE (site fixes 2026-10-21, Matt: "listings
 * with $1 prices must not appear with a $1 price in carousels, and must not
 * drag the price filter dial minimum down to $1").
 *
 * The MLS carries token asks on live rows: tenancy-in-common shares filed at
 * ListPrice 1 (the Redtail Hawk rows), and the $1.32 call-for-price ask
 * lib/atlas/pin-price.ts records from live Bend new construction on
 * 2026-09-19. None of them is what the home costs. A carousel card that
 * prints one tells a buyer a house is for sale at $1, and a price range read
 * from the cheapest ask in a set opens at $1.
 *
 * THE FLOOR. A sale ask under PLAUSIBLE_SALE_PRICE_FLOOR ($1,000) is a
 * placeholder. It is SALE_ASK_PLACEHOLDER_FLOOR, the same floor
 * publishSaleAskAmount applies, so a card, a search row and a price bound
 * all withhold the same rows. It is not the map pins' $10,000: a real in-park
 * manufactured home can ask $8,500 (ci:listing-figure-publish), and a floor
 * that high would hide real homes. A $3,000 ask therefore still prints on a
 * card; the pin keeps its own higher floor.
 *
 * A COMMERCIAL LEASE (MLS 'G') is out of scope: its ListPrice is rent, often
 * a dollar or two per square foot ("$1.40/sq ft/mo"), so the sale floor would
 * drop every real rate. A lease only needs a positive, finite figure here;
 * the lease publishers (lib/listing/publish-lease-rate.ts) own the rest.
 *
 * Pure and client-safe: dial builders, the listing dial and server reads all
 * ask the same question.
 */
import {
  listingPriceIsLeaseRate,
  SALE_ASK_PLACEHOLDER_FLOOR,
} from '@/lib/listing/publish-listing-figure'

/** Below this a for-sale ListPrice is a placeholder, never an ask (see above). */
export const PLAUSIBLE_SALE_PRICE_FLOOR = SALE_ASK_PLACEHOLDER_FLOOR

/**
 * True when `price` is an ask a card may print and a price bound may start
 * from: finite, positive, and (on a sale listing) at least the floor.
 * `propertyType` is the MLS PropertyType code; omit it for a known sale row.
 */
export function isPlausibleListPrice(
  price: number | null | undefined,
  propertyType?: string | null,
): boolean {
  if (typeof price !== 'number' || !Number.isFinite(price) || price <= 0) return false
  if (listingPriceIsLeaseRate(propertyType)) return true
  return price >= PLAUSIBLE_SALE_PRICE_FLOOR
}

/** The price when it is plausible, otherwise null (the card prints its own withheld line). */
export function plausibleListPrice(
  price: number | null | undefined,
  propertyType?: string | null,
): number | null {
  return isPlausibleListPrice(price, propertyType) ? (price as number) : null
}
